import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductCard from '../components/ProductCard';
import { useScrollReveal } from '../hooks/useGsapMotion';

const categoryLabels = {
  'computer-accessories': 'Computer Accessories',
  'audio-sound': 'Audio & Sound',
  'smart-devices': 'Smart Devices',
  'power-storage': 'Power & Storage',
  'home-appliances': 'Home Appliances',
  gaming: 'Gaming',
  'mobile-phone': 'Mobile Phones',
  smartwatch: 'Smart Watches',
  camera: 'Cameras',
  headphones: 'Headphones'
};

const Category = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const pageRef = useRef(null);

  useScrollReveal(pageRef, [loading, products.length, sortBy, category]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const categoryQuery = query(collection(db, 'products'), where('category', '==', category));
        const querySnapshot = await getDocs(categoryQuery);
        const data = querySnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        setProducts(data);
      } catch (error) {
        console.error('Error fetching category products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  const sortedProducts = useMemo(() => {
    const data = [...products];
    data.sort((a, b) => {
      const field = getSortField(sortBy);
      const direction = getSortDirection(sortBy);

      let aValue = a[field] || '';
      let bValue = b[field] || '';

      if (field === 'price' || field === 'rating') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (field === 'createdAt') {
        aValue = aValue?.toDate?.() || new Date(0);
        bValue = bValue?.toDate?.() || new Date(0);
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      return direction === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });
    return data;
  }, [products, sortBy]);

  if (loading) return <LoadingSpinner />;

  const categoryTitle =
    categoryLabels[category] ||
    category
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <div ref={pageRef} className="sc-container py-8 md:py-10">
      <section className="sc-soft-card mb-7 p-6" data-reveal>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Category</p>
        <h1 className="text-3xl font-black text-[#1A1A1A] md:text-4xl">{categoryTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''} available
        </p>

        <div className="mt-5 max-w-xs">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
            Sort products
          </label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sc-input">
            <option value="name">Name (A-Z)</option>
            <option value="price-low">Price (Low to High)</option>
            <option value="price-high">Price (High to Low)</option>
            <option value="rating">Rating (High to Low)</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </section>

      {sortedProducts.length === 0 ? (
        <div className="sc-soft-card p-12 text-center" data-reveal>
          <h3 className="text-xl font-bold text-slate-700">No products in this category yet</h3>
          <p className="mt-2 text-sm text-slate-500">Check back soon for updated catalog items.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4" data-stagger>
          {sortedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

const getSortField = (sortBy) => {
  switch (sortBy) {
    case 'price-low':
    case 'price-high':
      return 'price';
    case 'rating':
      return 'rating';
    case 'newest':
      return 'createdAt';
    default:
      return 'name';
  }
};

const getSortDirection = (sortBy) => {
  switch (sortBy) {
    case 'price-high':
    case 'rating':
    case 'newest':
      return 'desc';
    default:
      return 'asc';
  }
};

export default Category;
